import React, { useContext, useEffect } from 'react';
import { aiAssistantContext } from '../../../../../state/AIAssistantContextProvider';
import { Stack, IconButton, SvgIcon, CircularProgress, Typography, Box, Chip } from '@mui/material';
import getMuiIcon from '../../../../../utils/getMuiIcon';
import { borderRadius, palette, spaces } from '../../../../../assets/styles/themes/tokens';
import getTextTruncationSx from '../../../../../utils/getTextTruncationSx';
import { useSelector } from 'react-redux';
import {
  getAttachment as getAttachmentSelector,
  getAttachmentStatus
} from '../../../../../reducers/aiAssistant.selectors';
import { AsyncActionStatus } from '../../../../../actions/common.actionTypes';
import useAction from '../../../../../utils/useAction';
import {
  getAttachment as getAttachmentAction,
  removeTemporaryAttachment as removeTemporaryAttachmentAction
} from '../../../../../actions/aiAssistant.actions';

type AIUploadFileProps = {
  hashName: string;
  withRemoveButton?: boolean;
};

const AIUploadFile: React.FC<AIUploadFileProps> = ({
  hashName,
  withRemoveButton = false
}) => {
  const { aiFileUploadAbortControllerRef } = useContext(aiAssistantContext);
  const attachment = useSelector(state => getAttachmentSelector(state, hashName));
  const attachmentStatus = useSelector(state => getAttachmentStatus(state, hashName));
  const isLoading = attachmentStatus === AsyncActionStatus.LOADING || attachmentStatus === AsyncActionStatus.INITIAL;
  const getAttachment = useAction(getAttachmentAction);
  const removeTemporaryAttachment = useAction(removeTemporaryAttachmentAction);

  useEffect(() => {
    if (attachmentStatus === AsyncActionStatus.INITIAL) {
      getAttachment({ attachmentHashName: hashName });
    }
  }, [attachmentStatus, hashName, getAttachment]);

  const handleXClick = async () => {
    if (aiFileUploadAbortControllerRef?.current) {
      aiFileUploadAbortControllerRef?.current?.abort();
    } else {
      removeTemporaryAttachment({ attachmentHashName: hashName });
    }
  };

  const stackProps = isLoading ? 
    {
      alignItems: 'center',
      justifyContent: 'center',
    } : {
      alignItems: 'flex-start',
      justifyContent: 'space-between',
    };

  const typographySx = {
    ...getTextTruncationSx(2),
    wordBreak: 'break-word',
  };

  const wrapperSx = attachment?.data?.thumbnailUrl ? {
    backgroundImage: `url(${attachment.data.thumbnailUrl})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  }: !isLoading && {
    border: `2px solid ${palette.elevation.outlined}`
  };

  return (
    <Stack
      data-testid='ai-upload-file'
      sx={{
        position: 'relative',
        width: 80,
        height: 80,
        borderRadius: borderRadius.md,
        ...wrapperSx,
        backgroundColor: isLoading ? palette.action.hover : palette.secondary.contrastText,
      }}
      direction='column'
      {...stackProps}
    >
      {withRemoveButton && (
        <IconButton
          sx={{ position: 'absolute', top: 0, right: 0, color: palette.secondary.main }}
          onClick={handleXClick}
        >
          <SvgIcon viewBox='0 0 35 35' component={getMuiIcon('closeCircle')} />
        </IconButton>
      )}
      {isLoading && (
        <CircularProgress
          sx={{
            '& .MuiCircularProgress-svg': {
              width: 'auto',
              height: 'auto',
            }
          }}
          size={40}
        />
      )}
      {(!isLoading && !attachment?.data?.thumbnailUrl) && (
        <>
          <Box sx={{ padding: spaces.xs, paddingBottom: 0 }}>
            <Typography variant='subtitle3' sx={typographySx}>
              {attachment.data?.filename}
            </Typography>
          </Box>
          <Box sx={{ padding: spaces.xxs }}>
            <Chip 
              variant='outlined' 
              size='xsmall' 
              label={attachment.data?.extension?.toUpperCase()}
            />
          </Box>
        </>
      )}
    </Stack>
  );
};

export default AIUploadFile;
